import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FileUploader } from "ng2-file-upload";
import { filesize } from "filesize";
import { ToastrService } from "ngx-toastr";
import { parseCsvFile } from "app/main/helpers/papparse.helpper";
import { flatten } from "lodash";

type Bytes = number;
type UploadConfig = {
  // MIME types permitidos, separados por coma: "text/csv,application/vnd.ms-excel"
  allowedTypes?: ("csv" | "xlsx")[];

  // Tamaño máximo en bytes
  maxSize?: Bytes;

  // Si se permiten archivos sin filas
  allowEmptyRows?: boolean;

  // Lista de nombres de columnas que deben estar presentes
  requiredColumns?: string[];

  // Máxima cantidad de filas que puede contener el archivo
  maxRows?: number;
};

const defaultConfig: UploadConfig = {
  allowEmptyRows: false,
  maxSize: 1048576, // 1 MB
};

const fileTypeMimes = {
  csv: [
    "text/csv",
    "application/vnd.ms-excel",
    "text/plain",
    "application/csv",
  ],
};

@Component({
  selector: "app-file-upload",
  templateUrl: "./file-upload.component.html",
  styleUrls: ["./file-upload.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileUploadComponent implements OnInit {
  @ViewChild("fileInput") fileInput!: ElementRef<HTMLInputElement>;
  @Input() config: UploadConfig = {};
  @Output() fileSelected = new EventEmitter<{ file: File; rows: any[] }>();

  public uploader: FileUploader = new FileUploader({
    isHTML5: true,
    queueLimit: 1,
    removeAfterUpload: true,
    allowedFileType: ["csv"],
  });
  public hasBaseDropZoneOver: boolean = false;
  public currentFile: File = null;
  public fileRows: any[] = [];

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly toastrService: ToastrService
  ) {}

  ngOnInit(): void {
    console.log("Upload file config:", this.config);
    this.config = {
      ...defaultConfig,
      ...this.config,
    };
  }

  onFileSelected = async ([file]) => {
    console.log("On file selected: ", file);
    if (!file || !this.isValidType(file) || !this.isValidSize(file)) return;
    this.fileRows = await parseCsvFile(file);
    if (!this.isValidRows()) return;

    this.currentFile = file;
    this.fileSelected.emit({ file: this.currentFile, rows: this.fileRows });
    this.cdr.markForCheck();
  };

  removeFile = () => {
    this.currentFile = null;
    this.fileInput.nativeElement.value = "";
  };

  isValidSize = (file: File): boolean => {
    if (this.config.maxSize && file.size > this.config.maxSize) {
      const maxMB = (this.config.maxSize / (1024 * 1024)).toFixed(2);
      this.toastrService.warning(
        `El archivo supera el tamaño máximo permitido de ${maxMB} MB`
      );
      return false;
    }
    return true;
  };

  isValidType = (file: File): boolean => {
    if (this.config.allowedTypes && this.config.allowedTypes.length > 0) {
      const isAllow = this.mimeTypes.includes(file.type);
      if (!isAllow) {
        this.toastrService.warning("Tipo de archivo no permitido");
        return false;
      }
    }
    return true;
  };

  isValidRows = (): boolean => {
    const { requiredColumns, maxRows } = this.config;
    const hasRows = Array.isArray(this.fileRows) && this.fileRows.length > 0;
    if (!hasRows && this.config.allowEmptyRows === false) {
      this.toastrService.warning("El archivo no contiene filas de datos.");
      return false;
    }

    if (requiredColumns) {
      const fileColumns = Object.keys(this.fileRows[0] || {});
      const missingColumns = requiredColumns.filter(
        (col) => !fileColumns.includes(col)
      );
      console.log("Columns: ", {
        requiredColumns,
        missingColumns,
        fileColumns,
      });

      if (missingColumns.length > 0) {
        this.toastrService.warning(
          `${missingColumns.join(", ")}`,
          "Faltan columnas requeridas:",
          { timeOut: 1000 * 7 }
        );
        return false;
      }
    }

    if (maxRows && this.fileRows.length > maxRows) {
      this.toastrService.warning(
        `Máxima cantidad de filas permitidas ${maxRows}`
      );
      return false;
    }
    return true;
  };

  get fileSize() {
    return filesize(this.currentFile?.size ?? 0, { standard: "jedec" });
  }

  get maxSize() {
    return filesize(this.config.maxSize, { standard: "jedec" });
  }

  get fileName() {
    return this.currentFile?.name;
  }

  get mimeTypes() {
    return flatten(this.config.allowedTypes.map((type) => fileTypeMimes[type]));
  }

  get allowedTypes() {
    return this.config.allowedTypes.map((type) => `*.${type}`).join(",");
  }
}
